import { DxButton, DxGrid, DxModal, DxText } from '@dvag/design-system-react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { SavedFundDto, useFundsAutosave } from 'hooks/useFundsAutosave';
import { checkIcon, mobileBreakpoint } from 'utils/util';
import { FondDetail } from './component/FondDetail';
import { LeftFondList } from './component/LeftFondList';

import { FieldErrors } from 'react-hook-form';
import { FondDto } from '../../graphql/advisor/generates';
import { useGetAllFunds } from '../../hooks/useGetAllFunds';
import { useGetUserFonds } from '../../hooks/useGetUserFonds';
import { checkIsMobile } from '../../utils/windowSize';
import './FundsModal.css';

interface Props {
  visible: boolean;
  onClose: () => void;
}

const firstInvalidIndex = (err: FieldErrors<FondDto>): number => {
  if (!err || typeof err !== 'object') {
    return -1;
  }

  const [firstKey] = Object.keys(err);
  const bucket = firstKey ? (err as Record<string, unknown>)[firstKey] : undefined;

  if (!Array.isArray(bucket)) {
    return -1;
  }

  return bucket.findIndex(
    (entry) => entry !== undefined && Object.keys(entry as object).length > 0,
  );
};

export const FundsModal: React.FC<Props> = ({ visible, onClose }) => {
  const { t } = useTranslation();
  const { data: userFonds, isLoading: userFondsLoading } = useGetUserFonds();

  const { data: allFunds = [], isLoading: allFundsLoading } = useGetAllFunds(visible);

  const {
    loading,
    funds,
    register,
    selectedIndex,
    selectFund,
    appendFund,
    removeFund,
    setValue,
    flushChanges: save,
    disabledIsins,
    triggerValidation,
    getValues,
    errors,
    isDirty,
  } = useFundsAutosave(onClose, userFonds?.fonds as SavedFundDto[]);

  const runWithValidation = async (action: () => void) => {
    const isValid = await triggerValidation(true);

    if (!isValid) {
      const idx = firstInvalidIndex(errors);
      selectFund(idx !== -1 ? idx : funds.length - 1);
      return;
    }

    action();
  };

  const handleAddClick = () => runWithValidation(appendFund);

  const handleSelect = (idx: number) => selectFund(idx);

  const handleRemove = useCallback(
    (idx: number) => {
      removeFund(idx);
    },
    [removeFund],
  );

  const onConfirm = () => runWithValidation(save);

  return (
    <DxModal
      width="xxl"
      id="funds-selection-modal"
      className="funds-modal"
      data-testid="funds-selection-modal"
      visible={visible}
      label={t('fundsModal.title')}
      height={928}
      color="headline"
      onModalClosed={onClose}
    >
      <div slot="content" className="funds-modal-content" data-testid="funds-modal-content">
        <DxGrid
          id="funds-grid"
          className="funds-grid"
          rowgap="32h"
          mq2="12/12/12"
          mq3="12/5-7"
          data-testid="funds-grid"
        >
          <div className="funds-grid-top" data-testid="funds-grid-top">
            <DxText type="h5" data-testid="funds-main-text">
              {t('fundsModal.mainText')}
            </DxText>
          </div>

          <div data-testid="fond-list-wrapper">
            <LeftFondList
              funds={funds}
              selectedIndex={selectedIndex}
              onAdd={handleAddClick}
              onSelect={handleSelect}
              onRemove={handleRemove}
            />
          </div>

          <div data-testid="fond-detail-wrapper">
            {funds?.[selectedIndex] && (
              <FondDetail
                setAutosaveValue={setValue}
                register={register}
                getValues={getValues}
                disabledIsins={disabledIsins}
                removeCurrentFund={() => removeFund(selectedIndex)}
                allFunds={allFunds}
                resourceIndex={selectedIndex}
              />
            )}
          </div>
        </DxGrid>
      </div>

      <div slot="custom-actions">
        <DxButton
          data-testid="funds-confirm-btn"
          id="funds-confirm-btn"
          className="funds-confirm-btn"
          onClick={onConfirm}
          icon={checkIcon('check')}
          iconposition="right"
          stretch={checkIsMobile(mobileBreakpoint)}
          type="primary-m"
          label={t('fundsModal.confirm')}
          loading={loading || allFundsLoading || userFondsLoading}
          disabled={!isDirty}
        />
      </div>
    </DxModal>
  );
};
